import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/approve`);
      toast.success('Order approved! Customer can now access products');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to approve order');
    }
  };

  const handleReject = async (orderId) => {
    if (!window.confirm('Are you sure you want to reject this order?')) return;
    
    try {
      await api.post(`/orders/${orderId}/reject`);
      toast.success('Order rejected');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to reject order');
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'pending') return !order.admin_approved && order.payment_status !== 'rejected';
    if (filter === 'approved') return order.admin_approved;
    if (filter === 'rejected') return order.payment_status === 'rejected';
    return true; // all
  });

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading orders...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-orders-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-8" data-testid="orders-title">All Orders</h1>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              filter === 'all'
                ? 'bg-primary text-primary-foreground'
                : 'bg-white/5 text-foreground hover:bg-white/10'
            }`}
            data-testid="filter-all"
          >
            All Orders ({orders.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              filter === 'pending'
                ? 'bg-orange-500 text-white'
                : 'bg-white/5 text-foreground hover:bg-white/10'
            }`}
            data-testid="filter-pending"
          >
            Pending ({orders.filter(o => !o.admin_approved && o.payment_status !== 'rejected').length})
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              filter === 'approved'
                ? 'bg-accent text-white'
                : 'bg-white/5 text-foreground hover:bg-white/10'
            }`}
            data-testid="filter-approved"
          >
            Approved ({orders.filter(o => o.admin_approved).length})
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all ${
              filter === 'rejected'
                ? 'bg-destructive text-white'
                : 'bg-white/5 text-foreground hover:bg-white/10'
            }`}
            data-testid="filter-rejected"
          >
            Rejected ({orders.filter(o => o.payment_status === 'rejected').length})
          </button>
        </div>

        {filteredOrders.length > 0 ? (
          <div className="space-y-6">
            {filteredOrders.map((order) => (
              <div key={order.id} className="glass-card p-6" data-testid={`order-${order.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-xl font-bold mb-1">Order #{order.id.slice(0, 8)}</p>
                    <p className="text-sm text-muted-foreground">
                      User: {order.user_id.slice(0, 8)} • {new Date(order.created_at).toLocaleDateString()}
                    </p>
                    <p className="text-sm mt-1">
                      <span className="font-medium">Payment Method:</span> {order.payment_method}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary mb-1">₹{order.total_amount.toFixed(2)}</p>
                    <div className="flex flex-col gap-2">
                      <span className={`text-xs px-3 py-1 rounded-full ${
                        order.payment_status === 'completed'
                          ? 'bg-accent/20 text-accent'
                          : order.payment_status === 'pending'
                          ? 'bg-muted/20 text-muted-foreground'
                          : 'bg-destructive/20 text-destructive'
                      }`}>
                        {order.payment_status}
                      </span>
                      
                      {order.admin_approved ? (
                        <span className="text-xs px-3 py-1 rounded-full bg-accent/20 text-accent">
                          ✓ Approved
                        </span>
                      ) : (
                        <span className="text-xs px-3 py-1 rounded-full bg-orange-500/20 text-orange-400">
                          Pending Approval
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <p className="text-sm font-semibold mb-3">Items ({order.items.length})</p>
                  <div className="space-y-2">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center text-sm">
                        <span>{item.name} ({item.product_type})</span>
                        <span className="font-semibold">₹{item.price}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Approve/Reject Buttons */}
                {!order.admin_approved && order.payment_status !== 'rejected' && (
                  <div className="border-t border-white/10 pt-4 mt-4 flex gap-3">
                    <button
                      onClick={() => handleApprove(order.id)}
                      className="flex-1 px-4 py-3 bg-accent/20 text-accent rounded-xl hover:bg-accent/30 transition-all font-semibold flex items-center justify-center gap-2"
                      data-testid={`approve-order-${order.id}`}
                    >
                      <CheckCircle className="w-5 h-5" />
                      Approve Order
                    </button>
                    <button
                      onClick={() => handleReject(order.id)}
                      className="flex-1 px-4 py-3 bg-destructive/20 text-destructive rounded-xl hover:bg-destructive/30 transition-all font-semibold flex items-center justify-center gap-2"
                      data-testid={`reject-order-${order.id}`}
                    >
                      <XCircle className="w-5 h-5" />
                      Reject Order
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 glass-card" data-testid="no-orders">
            <h2 className="text-2xl font-bold mb-4">
              {filter === 'all' ? 'No orders yet' : `No ${filter} orders`}
            </h2>
            <p className="text-muted-foreground">
              {filter === 'all' 
                ? 'Orders will appear here once customers start purchasing'
                : `No orders in ${filter} status`
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
